import * as React from 'react'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import AccountIcon from '@mui/icons-material/AccountCircle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import VendorContactInfoCardMenu from 'vendors/components/vendorContact/VendorContactInfoCardMenu'
import { useUserContactReceivesRfps, useUserContactDoesNotReceiveRfps } from 'vendors/queries/useVendorQuery'
import { Tooltip } from '@mui/material'
import { Navigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useVendorWizard } from 'vendors/vendorWizard/VendorWizardContext'

type VendorUserContactCardProps = {
  onEdit: (userid: string) => void
  onDelete: (contactId: string, contactEmail: string) => void
  contact: VendorUserContactType
  vendorId: string
  isDeleteDisabled: boolean
}

const VendorUserContactCard: React.FC<VendorUserContactCardProps> = ({
  onEdit,
  onDelete,
  contact,
  vendorId,
  isDeleteDisabled
}) => {
  const { t } = useTranslation()
  const { mutate: receivesRfps, isError: receivesRfpsError } = useUserContactReceivesRfps(vendorId, contact.id)
  const { mutate: doesNotReceiveRfps, isError: doesNotReceiveRfpsError } = useUserContactDoesNotReceiveRfps(
    vendorId,
    contact.id
  )
  const { vendorUser } = useVendorWizard()

  const handleReceiveToggle = () => {
    if (contact.receiveRequestForProposals) {
      doesNotReceiveRfps()
    } else {
      receivesRfps()
    }
  }

  const handleEditClick = () => {
    onEdit(contact.id)
  }

  const handleDeleteClick = () => {
    onDelete(contact.id, contact.email)
  }

  if (receivesRfpsError || doesNotReceiveRfpsError) {
    return <Navigate to="/error" replace />
  }

  return (
    <Card sx={{ width: '99%', maxWidth: 420, mx: 1 }}>
      <CardContent sx={{ backgroundColor: 'grey.200' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 'fontWeightMedium' }}>{contact.email}</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {(contact.completedVendorFlow || contact.email === vendorUser?.email) && (
              <Tooltip title={`${contact.firstName} ${t('has-already-registered')}`}>
                <AccountIcon sx={{ color: 'grey.700' }} />
              </Tooltip>
            )}
            <VendorContactInfoCardMenu
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              isDeleteDisabled={isDeleteDisabled}
            />
          </Box>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {contact.firstName} {contact.lastName}
        </Typography>
        {contact.phone && (
          <Typography variant="body2" color="textSecondary">
            {contact.phone}
          </Typography>
        )}
        {contact.responsibilityDescription && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {contact.responsibilityDescription}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ px: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={contact.receiveRequestForProposals} onChange={handleReceiveToggle} />}
          label={t('will-receive-request-for-proposals')}
        />
      </CardActions>
    </Card>
  )
}

export default VendorUserContactCard
